import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  shouldShowBanner,
  acceptAllCookies,
  rejectNonEssentialCookies,
  saveConsentPreferences,
} from '@/global/utils/cookieConsent'
import { CookieIcon, ShieldCheckIcon, BarChart3Icon } from 'lucide-react'

export function CookieConsentBanner() {
  const [isOpen, setIsOpen] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Show banner on mount if needed with 5 second delay
    const shouldShow = shouldShowBanner()
    if (shouldShow) {
      const timer = setTimeout(() => {
        setIsOpen(true)
        // Small delay for fade-in effect
        setTimeout(() => setIsVisible(true), 50)
      }, 5000)
      
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAcceptAll = () => {
    acceptAllCookies()
    setIsOpen(false)
    // Reload to initialize PostHog with consent
    window.location.reload()
  }

  const handleRejectNonEssential = () => {
    rejectNonEssentialCookies()
    setIsOpen(false)
  }

  const handleSavePreferences = () => {
    saveConsentPreferences({
      essential: true,
      functional: true,
      analytics: analyticsEnabled,
    })
    setIsOpen(false)
    
    // If analytics is enabled, reload to initialize PostHog
    if (analyticsEnabled) {
      window.location.reload()
    }
  }

  const handleManagePreferences = () => {
    setShowPreferences(true)
  }

  const handleBackToMain = () => {
    setShowPreferences(false)
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className={`max-w-[90vw] sm:max-w-[700px] md:max-w-[800px] lg:max-w-[900px] bg-background/60 transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        {!showPreferences ? (
          <>
            <DialogHeader className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-primary/10">
                  <CookieIcon className="h-5 w-5 text-primary" />
                </div>
                <DialogTitle className="text-2xl">We Value Your Privacy</DialogTitle>
              </div>
              <DialogDescription className="text-base leading-relaxed">
                We use cookies to enhance your experience and help us improve our platform. 
                <strong className="block mt-3 text-foreground">
                  We're genuinely trying to make our software better. By allowing analytics cookies, 
                  you help us understand how to serve you more effectively!
                </strong>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4">
              <Card className="border-blue-300/30 bg-blue-50/10">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <ShieldCheckIcon className="h-4 w-4 text-primary" />
                    <CardTitle className="text-sm">Essential & Functional Cookies</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs">
                    Required for login, security, and basic functionality. These cannot be disabled.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-blue-300/30 bg-blue-50/10">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <BarChart3Icon className="h-4 w-4 text-muted-foreground" />
                    <CardTitle className="text-sm">Analytics Cookies</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs">
                    Help us understand how you use our platform so we can improve it. Includes page views, 
                    feature usage, and general interaction patterns.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col items-center gap-4 pt-2">
              <Button
                onClick={handleAcceptAll}
                size="lg"
                className="w-full text-base py-6"
              >
                Accept All Cookies
              </Button>
              
              <div className="flex gap-4 text-sm">
                <button
                  onClick={handleRejectNonEssential}
                  className="text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
                >
                  Reject Non-Essential
                </button>
                <span className="text-muted-foreground">•</span>
                <button
                  onClick={handleManagePreferences}
                  className="text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
                >
                  Manage Preferences
                </button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                You can change your preferences at any time in Settings → Cookie Settings or read our{' '}
                <a href="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </a>
              </p>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Manage Cookie Preferences</DialogTitle>
              <DialogDescription>
                Choose which types of cookies you want to allow. Essential cookies cannot be disabled 
                as they are necessary for the site to function.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between space-x-4 rounded-lg border border-blue-300/30 p-4 bg-blue-50/10">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <ShieldCheckIcon className="h-4 w-4 text-primary" />
                    <Label htmlFor="essential" className="font-semibold">
                      Essential & Functional Cookies
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Required for authentication, security, and basic site functionality (sidebar state, theme preferences).
                  </p>
                </div>
                <Switch
                  id="essential"
                  checked={true}
                  disabled={true}
                  aria-label="Essential cookies (always enabled)"
                />
              </div>

              <div className="flex items-center justify-between space-x-4 rounded-lg border border-blue-300/30 p-4 bg-blue-50/10">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <BarChart3Icon className="h-4 w-4" />
                    <Label htmlFor="analytics" className="font-semibold">
                      Analytics Cookies
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Help us improve by tracking page views and feature usage. Your data helps us serve you better!
                  </p>
                </div>
                <Switch
                  id="analytics"
                  checked={analyticsEnabled}
                  onCheckedChange={setAnalyticsEnabled}
                  aria-label="Toggle analytics cookies"
                />
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={handleBackToMain}
                className="w-full sm:w-auto"
              >
                Back
              </Button>
              <Button
                onClick={handleSavePreferences}
                className="w-full sm:w-auto"
              >
                Save Preferences
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
